import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const GOLD = '#D89232';
const GOLD_INK = '#8F650F';
const TERRA = '#E07A5F';
const HAIRLINE = '1px solid rgba(216,146,50,0.4)';

const MONTHS_DE = ['Januar','Februar','März','April','Mai','Juni','Juli','August','September','Oktober','November','Dezember'];

const formatDate = (iso) => {
  if (!iso) return '';
  const dm = String(iso).match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (dm) return `${parseInt(dm[1], 10)}. ${MONTHS_DE[parseInt(dm[2], 10) - 1]} ${dm[3]}`;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' });
};

// Vorschau-Urkunde (reines CSS) für den Leerzustand
const PreviewCertificate = () => (
  <div className="relative flex-shrink-0" style={{ width: 380, maxWidth: '100%', transform: 'rotate(-2deg)' }} data-testid="cert-preview-card">
    <div className="bg-cream-100 shadow-soft" style={{ aspectRatio: '1 / 1.414', border: HAIRLINE, padding: 8 }}>
      <div className="w-full h-full flex flex-col items-center text-center px-6 pt-9 pb-8" style={{ border: HAIRLINE }}>
        <img src="/dh_logo_terra.png" alt="" className="w-10 h-10 object-contain" />
        <p className="font-mono text-[9.5px] uppercase font-semibold mt-4" style={{ letterSpacing: '0.28em', color: GOLD_INK }}>
          Teilnahme&shy;bescheinigung
        </p>
        <p className="font-display italic text-[26px] text-sand-500 mt-8">Dein Name</p>
        <div className="my-6" style={{ width: 48, height: 1, backgroundColor: GOLD }} />
        <p className="font-display text-[17px] text-espresso leading-snug px-2">Weiterbildung deiner Wahl</p>
        <p className="font-display italic text-[13px] text-sand-500 mt-2">mit Erfolg abgeschlossen</p>
        <div className="mt-auto">
          <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto" style={{ backgroundColor: TERRA }}>
            <span className="font-display text-[15px]" style={{ color: '#F5F0EA' }}>DH</span>
          </div>
        </div>
      </div>
    </div>
    {/* Sanfter Schleier */}
    <div className="absolute inset-0" style={{ backgroundColor: 'rgba(245,240,234,0.55)', backdropFilter: 'blur(2px)' }} aria-hidden />
    {/* BALD-Label */}
    <span
      className="absolute font-mono text-[10px] uppercase font-bold px-3 py-1"
      style={{ top: 14, right: -10, transform: 'rotate(6deg)', letterSpacing: '0.3em', color: GOLD_INK, border: `1px solid ${GOLD_INK}`, backgroundColor: '#F5F0EA' }}
    >
      Bald
    </span>
  </div>
);

const MyCertificatesPage = ({ getAuthHeader }) => {
  const [loading, setLoading] = useState(true);
  const [certificates, setCertificates] = useState([]);
  const [downloadingId, setDownloadingId] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/user/my-certificates`, getAuthHeader());
      setCertificates(res.data.certificates || []);
    } catch (e) {
      toast.error('Zertifikate konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const downloadPdf = async (key, url, filename, successMsg) => {
    try {
      setDownloadingId(key);
      const res = await axios.get(url, { ...getAuthHeader(), responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
      toast.success(successMsg);
    } catch (e) {
      toast.error('Download fehlgeschlagen');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDownloadCertificate = (cert) => downloadPdf(
    `cert-${cert.certificate_number}`,
    `${API}/user/my-certificates/${encodeURIComponent(cert.certificate_number)}/pdf`,
    `Zertifikat_${cert.certificate_number}.pdf`,
    'Zertifikat heruntergeladen'
  );

  const handleDownloadAttendance = (cert) => downloadPdf(
    `att-${cert.certificate_number}`,
    `${API}/user/attendance-confirmation/${encodeURIComponent(cert.course_id)}`,
    `Teilnahmebescheinigung_${cert.certificate_number}.pdf`,
    'Teilnahmebescheinigung heruntergeladen'
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="my-certificates-loading">
        <Loader2 className="w-8 h-8 text-terra animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-10" data-testid="my-certificates-page">
      {/* Kopf */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <span className="w-8 h-[2px] bg-terra" />
          <span className="text-[11px] uppercase tracking-[0.25em] font-bold text-terra">
            Deine Abschlüsse · MMXXVI
          </span>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-espresso leading-[1.05]">
          Deine <span className="italic text-terra">Abschlüsse</span>.
        </h1>
        <p className="text-espresso/60 mt-4 max-w-2xl">
          Alle Zertifikate und Teilnahmebescheinigungen — jederzeit als PDF.
        </p>
      </div>

      {certificates.length === 0 ? (
        /* Leerzustand — Vorschau-Urkunde */
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16 py-6" data-testid="my-certificates-empty">
          <PreviewCertificate />
          <div className="max-w-md text-center lg:text-left">
            <p className="font-display text-[28px] sm:text-[32px] text-espresso leading-snug">
              Dein erstes Zertifikat ist nur <span className="italic text-terra">einen Kurs</span> entfernt.
            </p>
            <a
              href="/dashboard"
              className="mt-8 inline-flex items-center justify-center h-[48px] px-9 rounded-full bg-transparent text-[13px] font-bold uppercase transition-colors"
              style={{ border: `1.5px solid ${TERRA}`, color: TERRA, letterSpacing: '0.18em' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = TERRA; e.currentTarget.style.color = '#F5F0EA'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = TERRA; }}
              data-testid="cert-empty-cta-primary"
            >
              Weiterlesen in deinen Kursen →
            </a>
          </div>
        </div>
      ) : (
        /* Urkunden-Galerie */
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="my-certificates-list">
          {certificates.map((cert) => (
            <div
              key={cert.certificate_number || cert.id}
              className="bg-cream-100 shadow-soft transition-transform duration-300 hover:-translate-y-1"
              style={{ border: HAIRLINE, padding: 8 }}
              data-testid={`cert-card-${cert.certificate_number}`}
            >
              <div className="flex flex-col h-full px-6 py-7 sm:px-8" style={{ border: HAIRLINE }}>
                {/* Kopfzeile der Urkunde */}
                <div className="flex items-center justify-between gap-3">
                  <p className="font-mono text-[9.5px] uppercase font-semibold" style={{ letterSpacing: '0.24em', color: GOLD_INK }}>
                    Bestätigt · {cert.total_ue} UE
                  </p>
                  <p className="font-mono text-[9.5px] uppercase text-sand-500" style={{ letterSpacing: '0.14em' }} data-testid={`cert-number-${cert.certificate_number}`}>
                    Nº {cert.certificate_number}
                  </p>
                </div>

                {/* Titel */}
                <h3 className="font-display text-[24px] text-espresso leading-snug mt-5">
                  {cert.course_title}
                </h3>
                <p className="font-display italic text-[14px] text-sand-600 mt-2">
                  Abgeschlossen am {formatDate(cert.issued_at)}
                </p>

                <div className="my-5" style={{ width: 48, height: 1, backgroundColor: GOLD }} />

                {/* Downloads als Textlinks */}
                <div className="mt-auto space-y-2.5">
                  <button
                    onClick={() => handleDownloadCertificate(cert)}
                    disabled={downloadingId === `cert-${cert.certificate_number}`}
                    className="group inline-flex items-center gap-2 text-[13.5px] font-semibold transition-colors disabled:opacity-50"
                    style={{ color: TERRA }}
                    data-testid={`download-cert-${cert.certificate_number}`}
                  >
                    {downloadingId === `cert-${cert.certificate_number}` && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    <span className="group-hover:underline underline-offset-4">Zertifikat als PDF herunterladen ↓</span>
                  </button>
                  <br />
                  <button
                    onClick={() => handleDownloadAttendance(cert)}
                    disabled={downloadingId === `att-${cert.certificate_number}`}
                    className="group inline-flex items-center gap-2 text-[13.5px] font-medium text-sand-600 hover:text-terra transition-colors disabled:opacity-50"
                    data-testid={`download-attendance-${cert.certificate_number}`}
                  >
                    {downloadingId === `att-${cert.certificate_number}` && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    <span className="group-hover:underline underline-offset-4">Teilnahmebescheinigung mit Inhalten ↓</span>
                  </button>
                  {cert.verify_url && (
                    <p>
                      <a
                        href={cert.verify_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-display italic text-[13px] text-sand-500 hover:text-terra transition-colors"
                        data-testid={`verify-link-${cert.certificate_number}`}
                      >
                        ✳ Echtheit online verifizieren
                      </a>
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Abschlusszeile */}
      <div className="pt-8 text-center">
        <div className="mx-auto mb-4" style={{ width: 60, height: 1, backgroundColor: GOLD }} />
        <p className="text-[10px] uppercase font-semibold text-sand-500" style={{ letterSpacing: '0.28em' }}>
          Dickies-Campus Campus · Wissen, das weiterhilft.
        </p>
      </div>
    </div>
  );
};

export default MyCertificatesPage;
